import React, { useState, useContext, useEffect } from "react";
import { FiSun, FiMoon, FiShoppingCart, FiMenu, FiX } from "react-icons/fi";
import { BASE_URL, API_TOKEN } from "../../../src/config";
import { ThemeContext } from "../../contexts/ThemeContext";
import { useLanguage } from "../LanguageContext";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import AuthPage from "../AuthPage/AuthPage"; // Import AuthPage
import { useCart } from "../../contexts/CartContext"; // Import CartContext

const Navbar = () => {
  const { isDarkMode, toggleDarkMode } = useContext(ThemeContext);
  const { isRTL, toggleLanguage } = useLanguage();
  const { cartProducts } = useCart(); // Access cartProducts from CartContext
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [showAuth, setShowAuth] = useState(false); // State to control AuthPage visibility
  const [username, setUsername] = useState(
    localStorage.getItem("username") || ""
  ); // State for username
  const navigate = useNavigate(); // Initialize useNavigate

  useEffect(() => {
    const fetchStoreLogo = async () => {
      try {
        const response = await fetch(`${BASE_URL}/user/store-setting`, {
          headers: {
            Authorization: `Bearer ${API_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const storelogoUrl = data.userSettings.storelogo.url;
        setLogoUrl(storelogoUrl);
      } catch (error) {
        console.error("Error fetching store logo:", error);
      }
    };

    fetchStoreLogo();
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.body.dir = isRTL ? "rtl" : "ltr";
  }, [isRTL]);

  const iconClass =
    "text-gray-500 hover:text-yellow-600 dark:text-gray-400 dark:hover:text-yellow-400 transition-colors duration-200";

  // Define the onClose function
  const handleCloseAuth = () => {
    setShowAuth(false);
  };

  // Function to handle login
  const handleLogin = (user) => {
    localStorage.setItem("username", user); // Store username in localStorage
    setUsername(user); // Update state
    setShowAuth(false); // Close AuthPage
  };

  // Function to handle logout
  const handleLogout = () => {
    localStorage.removeItem("username"); // Remove username from localStorage
    setUsername(""); // Clear username state
    const deviceId = Math.random().toString(36).substring(2, 7); // Generate a 5-length random string
    localStorage.setItem("device_id", deviceId); // Store device_id in localStorage
  };

  return (
    <nav
      className={`bg-white dark:bg-black shadow-lg ${isRTL ? "rtl" : "ltr"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex items-center justify-between h-20">
          {/* Mobile menu button and logo container */}
          <div className="flex items-center sm:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={iconClass + " mr-4"}
            >
              {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
            <div className="absolute right-4">
              {logoUrl && (
                <img className="h-12 w-auto" src={logoUrl} alt="Store Logo" />
              )}
            </div>
          </div>
          {/* Cart icon and count */}
          {/* <div className="flex items-center absolute right-4">
            <div className="bg-yellow-500 rounded-full p-1 mb-2">
              <p className="text-xs text-black">
                {cartProducts.reduce((acc, item) => acc + item.qty, 0)}
              </p>
            </div>
            <button
              onClick={() => navigate("/cart")}
              className={iconClass + " mb-2"}
            >
              <FiShoppingCart size={20} />
            </button>
          </div> */}
          {/* Desktop logo (centered) */}
          <div className="hidden sm:flex sm:items-center sm:justify-center flex-grow">
            <div>
              {logoUrl && (
                <img className="h-12 w-auto" src={logoUrl} alt="Store Logo" />
              )}
            </div>
          </div>
          {/* Desktop menu */}
          <div className="hidden sm:flex sm:items-center">
            <div className="flex items-center space-x-10">
              <button
                onClick={toggleDarkMode} // Update this line
                className={iconClass}
              >
                {isDarkMode ? <FiSun size={24} /> : <FiMoon size={24} />}
              </button>
              <div className="h-8 w-px bg-gray-300 dark:bg-gray-700"></div>
              {/* Product count above cart icon */}
              <div className="flex flex-col items-end ml-auto">
                <div className="bg-yellow-500 rounded-full px-1 py-0.5">
                  <p className="text-xs text-black">
                    {cartProducts.reduce((acc, item) => acc + item.qty, 0)}
                  </p>
                </div>
                <button
                  onClick={() => navigate("/cart")} // Navigate to cartitems
                  className={iconClass}
                >
                  <FiShoppingCart size={24} />
                </button>
              </div>
              <div className="h-8 w-px bg-gray-300 dark:bg-gray-700"></div>
              <button
                onClick={toggleLanguage}
                className={`${iconClass} text-sm`}
              >
                {isRTL ? "English" : "عربي"}
              </button>
              <div className="h-8 w-px bg-gray-300 dark:bg-gray-700"></div>
              {username ? ( // Conditional rendering for login/logout
                <>
                  <span className={`${iconClass} text-sm`}>{username}</span>
                  <button
                    onClick={handleLogout} // Logout function
                    className={`${iconClass} font-medium`}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowAuth(true)} // Open AuthPage on click
                  className={iconClass + " font-medium"}
                >
                  {isRTL ? "تسجيل الدخول" : "Login"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`sm:hidden ${isMenuOpen ? "block" : "hidden"}`}>
        <div className="px-2 pt-2 pb-3 space-y-4">
          <div className="flex flex-col items-start space-y-4">
            <div className="flex items-center justify-between w-full">
              <span className={`${iconClass} text-sm`}>
                {isDarkMode ? "Dark Mode" : "Light Mode"}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDarkMode}
                  onChange={toggleDarkMode}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-500"></div>
              </label>
            </div>
            <a href="/cart" className={`${iconClass} block`}>
              {isRTL ? "عربة التسوق" : "Shopping Cart"}
            </a>
            <div className="flex items-center justify-between w-full">
              <span className={`${iconClass} text-sm`}>
                {isRTL ? "English" : "العربية"}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isRTL}
                  onChange={toggleLanguage}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-500"></div>
              </label>
            </div>
            {/* Add login/logout button for mobile view */}
            {username ? ( // Conditional rendering for login/logout
              <button
                onClick={handleLogout} // Logout function
                className={`bg-yellow-500 text-white py-2 px-4 rounded-full w-full font-medium`}
              >
                Logout
              </button>
            ) : (
              <button
                onClick={() => setShowAuth(true)} // Open AuthPage on click
                className={`bg-yellow-500 text-white py-2 px-4 rounded-full w-full font-medium`}
              >
                {isRTL ? "تسجيل الدخول" : "Login"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* AuthPage Popup */}
      {showAuth && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <AuthPage onClose={handleCloseAuth} />
        </div>
      )}
    </nav>
  );
};

export default Navbar;
